import store from '@/store';
import CourseMedia from '@/common/getCourseMedia';
import { requireAuthenticatedSession } from '@/common/auth';
import { handleApiResponse } from '@/common/serviceResponse';
import { performRequest } from '@/common/network';

export default {
    shouldUseCourseCache(download = false, isTrainer = store.getters.getIsTrainer) {
        return isTrainer && Boolean(download);
    },

    async handleFailedCourseUpdate(error, storeObject, courseData) {
        if (store.getters.getIsTrainer) {
            await this.cacheRequest(storeObject, courseData);
            return;
        }
        throw error;
    },

    async getCourses() {
        const token = await requireAuthenticatedSession();
        const url = process.env.VUE_APP_APIURL + '/api/v1/courses/';
        const response = await performRequest(url, {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        }, {
            service: 'CourseService.getCourses',
            trainer: store.getters.getIsTrainer,
        });
        const responseJson = await handleApiResponse(response);
        if (!responseJson) {
            return responseJson;
        }

        responseJson.courses.forEach((course, index) => {
            let img = 'course_image';
            if (store.getters.getIsTrainer){
                img = 'course_image_url'
            }
            if (course[img] && !course[img].includes('s3.eu-west-2.amazonaws.com') && !course[img].startsWith('http')){
                course[img] = process.env.VUE_APP_APIURL + course[img];
            }
            if (!course[img]){
                course[img] = '/img/thumbnail.jpg';
            }
            responseJson.courses[index] = course;
        });

        return responseJson;
    },

    async getCourse(courseID, download, isTrainer = store.getters.getIsTrainer) {
        const token = await requireAuthenticatedSession();
        const url = process.env.VUE_APP_APIURL + '/api/v1/course/' + courseID + (this.shouldUseCourseCache(download, isTrainer) ? '?cache=true' : '');
        try {
            const response = await performRequest(url, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            }, {
                service: 'CourseService.getCourse',
                courseId: courseID,
                download,
                trainer: isTrainer,
            });
            const responseJson = await handleApiResponse(response);
            if (!responseJson) {
                return responseJson;
            }

            const course = await CourseMedia.getMediaURLS(responseJson.course, download, isTrainer);
            responseJson.course = course;
            const providerId = responseJson.course.provider_id || store.state.site.id;
            if (download){
                await this.getCompanyBrandingByHost(providerId);
            }
            return responseJson;
        } catch (error) {
            if (error?.status === 404 && !isTrainer) {
                await this.removeStaleLearnerCourse(courseID);
            }
            throw error;
        }
    },

    async removeStaleLearnerCourse(courseID) {
        store.dispatch('removeCourseFromRequestQueue', courseID);

        if (typeof caches === 'undefined') {
            return;
        }

        try {
            const cache = await caches.open('courses');
            const courseListUrl = process.env.VUE_APP_APIURL + '/api/v1/courses/';
            const cachedResponse = await cache.match(courseListUrl);

            if (!cachedResponse) {
                return;
            }

            const responseJson = await cachedResponse.clone().json();
            if (!Array.isArray(responseJson?.courses)) {
                return;
            }

            const courses = responseJson.courses.filter(course => String(course.id) !== String(courseID));
            if (courses.length === responseJson.courses.length) {
                return;
            }

            const headers = new Headers(cachedResponse.headers);
            headers.delete('Content-Encoding');
            headers.delete('Content-Length');
            headers.set('Content-Type', 'application/json');
            await cache.put(courseListUrl, new Response(JSON.stringify({
                ...responseJson,
                courses,
            }), {
                status: cachedResponse.status,
                statusText: cachedResponse.statusText,
                headers,
            }));
        } catch {
            // Cache cleanup is best effort; preserve the original API response.
        }
    },

    async startCourse(courseID, courseData) {
        const token = await requireAuthenticatedSession();
        const url = process.env.VUE_APP_APIURL + '/api/v1/course/' + courseID + '/start';
        try {
            const response = await performRequest(url, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            }, {
                service: 'CourseService.startCourse',
                courseId: courseID,
                courseData,
            });
            return await handleApiResponse(response);
        } catch (error) {
            await this.handleFailedCourseUpdate(error, {
                course: courseID,
                type: 'startCourse'
            }, courseData);
        }
    },

    async completeLesson(courseID, lessonID, courseData) {
        const token = await requireAuthenticatedSession();
        const payload = {
            lessonId: String(lessonID)
        };
        const url = process.env.VUE_APP_APIURL + '/api/v1/course/' + courseID + '/complete-lesson';
        try {
            const response = await performRequest(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            }, {
                service: 'CourseService.completeLesson',
                courseId: courseID,
                lessonId: lessonID,
                courseData,
            });
            return await handleApiResponse(response, {
                expectSuccess: true,
            });
        } catch (error){
            await this.handleFailedCourseUpdate(error, {
                course: courseID,
                type: 'completeLesson',
                data: lessonID
            }, courseData);
        }
    },
    async finishCourse(courseID, courseData) {
        const token = await requireAuthenticatedSession();
        const url = process.env.VUE_APP_APIURL + '/api/v1/course/' + courseID + '/finish';
        try {
            const response = await performRequest(url, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            }, {
                service: 'CourseService.finishCourse',
                courseId: courseID,
                courseData,
            });
            return await handleApiResponse(response);
        } catch (error) {
            await this.handleFailedCourseUpdate(error, {
                course: courseID,
                type: 'finishCourse',

            }, courseData);
        }
    },
    async submitQuiz(courseID, quizData, courseData) {
        const token = await requireAuthenticatedSession();
        const payload = {
            response_data: quizData
        };
        const url = process.env.VUE_APP_APIURL + '/api/v1/course/' + courseID + '/submit-update';
        try {
            const response = await performRequest(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            }, {
                service: 'CourseService.submitQuiz',
                courseId: courseID,
                courseData,
                quizData,
            });
            return await handleApiResponse(response);
        } catch (error){
            await this.handleFailedCourseUpdate(error, {
                course: courseID,
                type: 'submitQuiz',
                data: quizData
            }, courseData);
        }
    },
    async getCompanyBrandingByHost(id) {
        const token = await requireAuthenticatedSession();
        let url = process.env.VUE_APP_APIURL + '/api/company-branding-host-login?company_id=' + id;
        const response = await performRequest(url, {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        }, {
            service: 'CourseService.getCompanyBrandingByHost',
            companyId: id,
        });
        const responseJson = await handleApiResponse(response);
        if (responseJson?.message === 'OK: Successfully retrieved company branding'){
            return responseJson;
        }

        throw new Error(response.statusText);
    },
    async cacheRequest(storeObject, courseData){
        const cache = await caches.open('courses');
        let courseCache = await cache.match(process.env.VUE_APP_APIURL + '/api/v1/course/' + storeObject.course + '?cache=true');
        if (courseData && courseCache){
            const newResponse = new Response(JSON.stringify(courseData), {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            await cache.put(process.env.VUE_APP_APIURL + '/api/v1/course/' + storeObject.course + '?cache=true', newResponse);
            store.dispatch('addToRequestQueue', storeObject);
        } else if (courseCache) {
            store.dispatch('addToRequestQueue', storeObject);
        } else {
            //store.dispatch('toasting', 'offlineNotDownloaded');
        }
    }
};
